import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Card, Form, Row, Col, Button, Badge, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Ticket, PurchaseRequest, Workflow, Group, User } from '../types';
import { API_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import { useArea } from '../context/AreaContext';
import { useModal } from '../context/ModalContext';
import { getPurchaseDisplayId } from '../utils/purchaseDisplayId';
import './Reports.css';
import {
  getWorkflowPriorityVariant,
  getWorkflowStatusLabel,
  getWorkflowStatusVariant,
} from '../utils/workflowVisuals';

interface WorkflowStep {
  id?: string;
  name: string;
  status?: string;
  description?: string;
  order?: number;
}

interface KanbanColumn {
  id: string;
  name: string;
  status: string;
  step?: string;
  order?: number;
}

const Reports: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { currentArea } = useArea();
  const { showAlert } = useModal();

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [purchaseRequests, setPurchaseRequests] = useState<PurchaseRequest[]>([]);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Filtros
  const [selectedWorkflow, setSelectedWorkflow] = useState<number | ''>('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterGroup, setFilterGroup] = useState<number | ''>('');
  const [filterUser, setFilterUser] = useState<number | ''>('');
  const [filterAssignedTo, setFilterAssignedTo] = useState<number | ''>('');
  const [searchText, setSearchText] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Drag and drop states
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [draggingTicket, setDraggingTicket] = useState<Ticket | null>(null);
  const [draggingPurchaseRequest, setDraggingPurchaseRequest] = useState<PurchaseRequest | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overColumnId, setOverColumnId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Requer movimento de pelo menos 8px antes de iniciar o drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchWorkflows();
    fetchGroups();
    if (currentUser?.role === 'admin' || currentUser?.role === 'analista') {
      fetchUsers();
    }
  }, [currentUser, currentArea]);

  // Mostrar modal de seleção de fluxo ao abrir a tela se nenhum estiver selecionado
  useEffect(() => {
    if (workflows.length > 0 && !selectedWorkflow) {
      setShowWorkflowModal(true);
    }
  }, [workflows, selectedWorkflow]);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      if (currentArea === 'purchases') {
        // Buscar purchase requests
        const params: any = {
          parent_request_id: 0, // Apenas requisições principais
          limit: 1000,
          // Relatório preserva o histórico: sem recorte por data de conclusão.
          closed_visibility: 'all',
        };
        
        if (selectedWorkflow) params.workflow_id = selectedWorkflow;
        if (filterStatus) params.status = filterStatus;
        if (filterPriority) params.priority = filterPriority;
        if (filterAssignedTo) params.assigned_to = filterAssignedTo;
        if (searchText) params.search = searchText;

        console.log('Reports: Buscando purchase requests com params:', params);
        const response = await axios.get(`${API_URL}/api/purchases`, { params });
        console.log('Reports: Resposta recebida:', response.data.length, 'requisições');
        
        // Aplicar filtros adicionais no frontend (grupo, usuário criador, data)
        let filteredRequests = response.data;
        
        if (filterGroup) {
          filteredRequests = filteredRequests.filter((pr: PurchaseRequest) => pr.assigned_group_id === filterGroup);
        }
        
        if (filterUser) {
          filteredRequests = filteredRequests.filter((pr: PurchaseRequest) => pr.created_by === filterUser);
        }
        
        if (dateFrom) {
          const fromDate = new Date(dateFrom);
          filteredRequests = filteredRequests.filter((pr: PurchaseRequest) => 
            new Date(pr.created_at) >= fromDate
          );
        }
        
        if (dateTo) {
          const toDate = new Date(dateTo);
          toDate.setHours(23, 59, 59, 999); // Incluir todo o dia
          filteredRequests = filteredRequests.filter((pr: PurchaseRequest) => 
            new Date(pr.created_at) <= toDate
          );
        }
        
        console.log('Reports: Requisições filtradas:', filteredRequests.length);
        console.log('Reports: Primeiras 3 requisições:', filteredRequests.slice(0, 3).map((pr: PurchaseRequest) => ({
          id: pr.id,
          title: pr.title,
          status: pr.status,
          workflow_id: pr.workflow_id,
          current_step: pr.current_step
        })));
        
        setPurchaseRequests(filteredRequests);
        setTickets([]);
      } else {
        // Buscar tickets
        const params: any = {
          parent_ticket_id: 0, // Apenas tickets principais
          limit: 1000,
          // Relatório preserva o histórico: sem recorte por data de conclusão.
          closed_visibility: 'all',
        };
        
        if (selectedWorkflow) params.workflow_id = selectedWorkflow;
        if (filterStatus) params.status = filterStatus;
        if (filterPriority) params.priority = filterPriority;
        if (filterAssignedTo) params.assigned_to = filterAssignedTo;
        if (searchText) params.search = searchText;

        const response = await axios.get(`${API_URL}/api/tickets`, { params });
        
        // Aplicar filtros adicionais no frontend (grupo, usuário criador, data)
        let filteredTickets = response.data;
        
        if (filterGroup) {
          filteredTickets = filteredTickets.filter((t: Ticket) => t.assigned_group_id === filterGroup);
        }
        
        if (filterUser) {
          filteredTickets = filteredTickets.filter((t: Ticket) => t.created_by === filterUser);
        }
        
        if (dateFrom) {
          const fromDate = new Date(dateFrom);
          filteredTickets = filteredTickets.filter((t: Ticket) => 
            new Date(t.created_at) >= fromDate
          );
        }
        
        if (dateTo) {
          const toDate = new Date(dateTo);
          toDate.setHours(23, 59, 59, 999); // Incluir todo o dia
          filteredTickets = filteredTickets.filter((t: Ticket) => 
            new Date(t.created_at) <= toDate
          );
        }
        
        setTickets(filteredTickets);
        setPurchaseRequests([]);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  }, [currentArea, selectedWorkflow, filterStatus, filterPriority, filterGroup, filterUser, filterAssignedTo, searchText, dateFrom, dateTo]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const fetchWorkflows = async () => {
    try {
      // Buscar workflows baseado na área atual
      if (currentArea === 'purchases') {
        const response = await axios.get(`${API_URL}/api/workflows/published`, {
          params: { area_type: 'purchases' }
        });
        setWorkflows(response.data);
      } else {
        const response = await axios.get(`${API_URL}/api/workflows`, {
          params: { area_type: 'tickets' }
        });
        setWorkflows(response.data.filter((w: Workflow) => w.is_active));
      }
    } catch (error) {
      console.error('Erro ao carregar fluxos:', error);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/groups`, {
        params: { limit: 1000 },
      });
      setGroups(response.data.filter((g: Group) => g.is_active));
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`);
      setUsers(response.data.filter((u: User) => u.is_active));
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  // Parse workflow steps
  const parseWorkflowSteps = (workflow: Workflow | null): WorkflowStep[] => {
    if (!workflow || !workflow.steps) return [];
    
    try {
      const stepsData = JSON.parse(workflow.steps);
      if (Array.isArray(stepsData)) {
        return stepsData.map((s: any, index: number) => 
          typeof s === 'string' 
            ? { name: s, status: 'em_andamento', order: index }
            : { 
                id: s.id || s.step_id,
                name: s.name || s, 
                status: s.status || 'em_andamento',
                description: s.description,
                order: typeof s.order === 'number' ? s.order : index,
              }
        );
      }
    } catch (error) {
      console.error('Erro ao parsear steps do workflow:', error);
    }
    
    return [];
  };

  // Obter workflow selecionado
  const selectedWorkflowData = useMemo(() => {
    if (!selectedWorkflow) return null;
    return workflows.find(w => w.id === selectedWorkflow) || null;
  }, [selectedWorkflow, workflows]);

  const buildDefaultColumns = useCallback((): KanbanColumn[] => {
    const baseColumns = [
      { id: 'novo', name: 'Novo', status: 'novo' },
      { id: 'aberto', name: 'Aberto', status: 'aberto' },
      { id: 'em_andamento', name: 'Em Andamento', status: 'em_andamento' },
      { id: 'aguardando_informacao_usuario', name: 'Aguardando Informação do Usuário', status: 'aguardando_informacao_usuario' },
      { id: 'aguardando_terceiros', name: 'Aguardando Terceiros', status: 'aguardando_terceiros' },
      { id: 'aguardando_evidencia', name: 'Aguardando Evidência', status: 'aguardando_evidencia' },
      { id: 'aguardando_problema', name: 'Aguardando Problema', status: 'aguardando_problema' },
      { id: 'aguardando_implantacao', name: 'Aguardando Implantação', status: 'aguardando_implantacao' },
      { id: 'resolvido', name: 'Resolvido', status: 'resolvido' },
      { id: 'cancelado', name: 'Cancelado', status: 'cancelado' },
      { id: 'fechado', name: 'Fechado', status: 'fechado' },
    ];

    if (currentArea === 'purchases') {
      baseColumns.unshift(
        { id: 'rascunho', name: 'Rascunho', status: 'rascunho' },
        { id: 'aguardando_aprovacao', name: 'Aguardando Aprovação', status: 'aguardando_aprovacao' },
        { id: 'aguardando_reenvio', name: 'Aguardando Reenvio', status: 'aguardando_reenvio' },
      );
    }
    return baseColumns;
  }, [currentArea]);

  // Obter colunas do Kanban baseadas no workflow
  const kanbanColumns = useMemo((): KanbanColumn[] => {
    if (!selectedWorkflowData) {
      return buildDefaultColumns();
    }

    const steps = parseWorkflowSteps(selectedWorkflowData);
    
    if (steps.length === 0) {
      return [];
    }

    // Com workflow selecionado, as colunas devem refletir exatamente os steps
    return steps
      .filter((step) => !!step.name)
      .map((step, index): KanbanColumn => ({
      id: step.id || `step-${index}-${step.name.toLowerCase().replace(/\s+/g, '-')}`,
      name: step.name,
      step: step.name,
      status: step.status || 'em_andamento',
      order: typeof step.order === 'number' ? step.order : index,
    }))
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [selectedWorkflowData, buildDefaultColumns]);

  // Agrupar tickets/purchase requests por coluna
  const ticketsByColumn = useMemo(() => {
    const grouped: { [key: string]: (Ticket | PurchaseRequest)[] } = {};
    
    kanbanColumns.forEach(column => {
      grouped[column.id] = [];
    });

    const items = currentArea === 'purchases' ? purchaseRequests : tickets;
    
    console.log('Reports: Agrupando items:', {
      currentArea,
      itemsCount: items.length,
      purchaseRequestsCount: purchaseRequests.length,
      ticketsCount: tickets.length,
      selectedWorkflow: selectedWorkflowData?.id,
      columnsCount: kanbanColumns.length
    });

    items.forEach((item: Ticket | PurchaseRequest) => {
      // Se há workflow selecionado, usar current_step para agrupar
      if (selectedWorkflowData && item.workflow_id === selectedWorkflowData.id && item.current_step) {
        const column = kanbanColumns.find((c: KanbanColumn) => c.step && c.step === item.current_step);
        if (column) {
          grouped[column.id].push(item);
        } else {
          // Fallback para status
          const statusColumn = kanbanColumns.find((c: KanbanColumn) => c.status === item.status);
          if (statusColumn) {
            grouped[statusColumn.id].push(item);
          } else {
            console.warn('Reports: Item não encontrou coluna:', {
              id: item.id,
              status: item.status,
              current_step: item.current_step,
              workflow_id: item.workflow_id
            });
          }
        }
      } else {
        // Agrupar por status
        const column = kanbanColumns.find((c: KanbanColumn) => c.status === item.status);
        if (column) {
          grouped[column.id].push(item);
        } else {
          console.warn('Reports: Item não encontrou coluna por status:', {
            id: item.id,
            status: item.status,
            availableStatuses: kanbanColumns.map(c => c.status)
          });
        }
      }
    });

    console.log('Reports: Items agrupados por coluna:', Object.keys(grouped).map(key => ({
      column: key,
      count: grouped[key].length
    })));

    return grouped;
  }, [tickets, purchaseRequests, currentArea, kanbanColumns, selectedWorkflowData]);

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      novo: 'primary',
      aberto: 'warning',
      em_andamento: 'info',
      rascunho: 'secondary',
      aguardando_aprovacao: 'primary',
      aguardando_informacao_usuario: 'secondary',
      aguardando_terceiros: 'secondary',
      aguardando_evidencia: 'secondary',
      aguardando_problema: 'secondary',
      aguardando_implantacao: 'secondary',
      resolvido: 'success',
      cancelado: 'danger',
      fechado: 'dark',
    };
    return colors[status] || 'secondary';
  };

  const getStatusLabel = (status: string): string => {
    const labels: { [key: string]: string } = {
      novo: 'Novo',
      aberto: 'Aberto',
      em_andamento: 'Em Andamento',
      rascunho: 'Rascunho',
      aguardando_aprovacao: 'Aguardando Aprovação',
      aguardando_reenvio: 'Aguardando Reenvio',
      aguardando_informacao_usuario: 'Aguardando Informação do Usuário',
      aguardando_terceiros: 'Aguardando Terceiros',
      aguardando_evidencia: 'Aguardando Evidência',
      aguardando_problema: 'Aguardando Problema',
      aguardando_implantacao: 'Aguardando Implantação',
      resolvido: 'Resolvido',
      cancelado: 'Cancelado',
      fechado: 'Fechado',
    };
    return labels[status] || status;
  };

  const getPriorityColor = (priority: string | undefined) => {
    const colors: { [key: string]: string } = {
      p1: 'danger',
      p2: 'warning',
      p3: 'info',
      p4: 'success',
    };
    return colors[priority ?? ''] || 'secondary';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const clearFilters = () => {
    setSelectedWorkflow('');
    setFilterStatus('');
    setFilterPriority('');
    setFilterGroup('');
    setFilterUser('');
    setFilterAssignedTo('');
    setSearchText('');
    setDateFrom('');
    setDateTo('');
  };

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (selectedWorkflow) count++;
    if (filterStatus) count++;
    if (filterPriority) count++;
    if (filterGroup) count++;
    if (filterUser) count++;
    if (filterAssignedTo) count++;
    if (searchText) count++;
    if (dateFrom) count++;
    if (dateTo) count++;
    return count;
  }, [selectedWorkflow, filterStatus, filterPriority, filterGroup, filterUser, filterAssignedTo, searchText, dateFrom, dateTo]);

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id as string);
    
    // Encontrar o item sendo arrastado (ticket ou purchase request)
    const itemId = parseInt(active.id as string);
    if (currentArea === 'purchases') {
      const purchaseRequest = purchaseRequests.find(pr => pr.id === itemId);
      setDraggingPurchaseRequest(purchaseRequest || null);
      setDraggingTicket(null);
    } else {
      const ticket = tickets.find(t => t.id === itemId);
      setDraggingTicket(ticket || null);
      setDraggingPurchaseRequest(null);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setDraggingTicket(null);
    setDraggingPurchaseRequest(null);
    setOverColumnId(null);

    if (!over || !selectedWorkflowData) return;

    const itemId = parseInt(active.id as string);
    const targetColumnId = over.id as string;
    
    if (currentArea === 'purchases') {
      const purchaseRequest = purchaseRequests.find(pr => pr.id === itemId);
      if (!purchaseRequest) return;

      // Encontrar a coluna de destino
      const targetColumn = kanbanColumns.find(c => c.id === targetColumnId);
      if (!targetColumn) return;

      // Encontrar a coluna atual da requisição
      const currentColumn = kanbanColumns.find(c => {
        if (selectedWorkflowData && purchaseRequest.workflow_id === selectedWorkflowData.id && purchaseRequest.current_step) {
          return c.step === purchaseRequest.current_step;
        }
        return c.status === purchaseRequest.status;
      });

      // Se está na mesma coluna, não fazer nada
      if (currentColumn?.id === targetColumnId) return;

      // Verificar se o usuário tem permissão
      const isResponsible = purchaseRequest.assigned_to === currentUser?.id || purchaseRequest.taken_by === currentUser?.id;
      const isAdmin = currentUser?.role === 'admin';
      
      if (!isAdmin && !isResponsible) {
        showAlert({
          title: 'Sem permissão',
          message: 'Apenas o responsável pela requisição pode alterar o status',
          variant: 'warning',
        });
        return;
      }

      try {
        // Preparar dados de atualização
        const updateData: any = {};
        
        // Se a coluna de destino tem um step, atualizar o current_step
        if (targetColumn.step) {
          updateData.current_step = targetColumn.step;
        } else {
          updateData.status = targetColumn.status;
          if (targetColumn.status === 'aberto') {
            updateData.current_step = null;
          }
        }

        // Atualizar a requisição via API
        await axios.put(`${API_URL}/api/purchases/${itemId}`, updateData);
        
        // Atualizar a requisição localmente
        setPurchaseRequests(prevRequests => 
          prevRequests.map(pr => 
            pr.id === itemId 
              ? { 
                  ...pr, 
                  status: targetColumn.status as any,
                  current_step: targetColumn.step || (targetColumn.status === 'aberto' ? undefined : pr.current_step),
                  updated_at: new Date().toISOString()
                }
              : pr
          )
        );
      } catch (error: any) {
        console.error('Erro ao atualizar requisição:', error);
        const errorMessage = error.response?.data?.detail || 'Erro ao atualizar requisição. Verifique se a transição é permitida pelo fluxo.';
        showAlert({
          title: 'Erro ao atualizar',
          message: errorMessage,
          variant: 'danger',
        });
      }
    } else {
      const ticket = tickets.find(t => t.id === itemId);
      if (!ticket) return;

      // Encontrar a coluna de destino
      const targetColumn = kanbanColumns.find(c => c.id === targetColumnId);
      if (!targetColumn) return;

      // Encontrar a coluna atual do ticket
      const currentColumn = kanbanColumns.find(c => {
        if (selectedWorkflowData && ticket.workflow_id === selectedWorkflowData.id && ticket.current_step) {
          return c.step === ticket.current_step;
        }
        return c.status === ticket.status;
      });

      // Se está na mesma coluna, não fazer nada
      if (currentColumn?.id === targetColumnId) return;

      // Verificar se o usuário tem permissão
      const isResponsible = ticket.assigned_to === currentUser?.id || ticket.taken_by === currentUser?.id;
      const isAdmin = currentUser?.role === 'admin';
      
      if (!isAdmin && !isResponsible) {
        showAlert({
          title: 'Sem permissão',
          message: 'Apenas o responsável pelo ticket pode alterar o status',
          variant: 'warning',
        });
        return;
      }

      try {
        // Preparar dados de atualização
        const updateData: any = {};
        
        // Se a coluna de destino tem um step, atualizar o current_step
        if (targetColumn.step) {
          updateData.current_step = targetColumn.step;
        } else {
          updateData.status = targetColumn.status;
          if (targetColumn.status === 'aberto') {
            updateData.current_step = null;
          }
        }

        // Atualizar o ticket via API
        await axios.put(`${API_URL}/api/tickets/${itemId}`, updateData);
        
        // Atualizar o ticket localmente
        setTickets(prevTickets => 
          prevTickets.map(t => 
            t.id === itemId 
              ? { 
                  ...t, 
                  status: targetColumn.status as any,
                  current_step: targetColumn.step || (targetColumn.status === 'aberto' ? undefined : t.current_step),
                  updated_at: new Date().toISOString()
                }
              : t
          )
        );
      } catch (error: any) {
        console.error('Erro ao atualizar ticket:', error);
        const errorMessage = error.response?.data?.detail || 'Erro ao atualizar ticket. Verifique se a transição é permitida pelo fluxo.';
        showAlert({
          title: 'Erro ao atualizar',
          message: errorMessage,
          variant: 'danger',
        });
      }
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over || !selectedWorkflowData) {
      setOverColumnId(null);
      return;
    }

    const itemId = parseInt(active.id as string);
    let item: Ticket | PurchaseRequest | undefined;
    if (currentArea === 'purchases') {
      item = purchaseRequests.find((pr: PurchaseRequest) => pr.id === itemId);
    } else {
      item = tickets.find((t: Ticket) => t.id === itemId);
    }
    if (!item) {
      setOverColumnId(null);
      return;
    }

    const targetColumnId = over.id as string;
    const targetColumn = kanbanColumns.find(c => c.id === targetColumnId);
    if (!targetColumn) {
      setOverColumnId(null);
      return;
    }

    // Encontrar a coluna atual do item (item não pode ser undefined aqui devido à verificação acima)
    const currentItem = item;
    const currentColumn = kanbanColumns.find(c => {
      if (selectedWorkflowData && currentItem.workflow_id === selectedWorkflowData.id && currentItem.current_step) {
        return c.step === currentItem.current_step;
      }
      return c.status === currentItem.status;
    });

    // Se está na mesma coluna, não destacar
    if (currentColumn?.id === targetColumnId) {
      setOverColumnId(null);
      return;
    }

    // Destacar a coluna de destino
    setOverColumnId(targetColumnId);
  };

  const items = currentArea === 'purchases' ? purchaseRequests : tickets;
  
  if (loading && items.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
      </div>
    );
  }

  // Componente para card sortable (genérico para Ticket ou PurchaseRequest)
  const SortableItemCard: React.FC<{ item: Ticket | PurchaseRequest }> = ({ item }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: item.id.toString() });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition: isDragging ? undefined : transition,
      opacity: isDragging ? 0.5 : 1,
    };

    const handleCardClick = (e: React.MouseEvent) => {
      // Prevenir navegação se o usuário estiver arrastando
      if (isDragging) return;
      
      // Abrir em nova janela
      e.preventDefault();
      e.stopPropagation();
      const url = currentArea === 'purchases' ? `/purchases/${item.id}` : `/tickets/${item.id}`;
      window.open(url, '_blank');
    };

    return (
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        <Card
          className="kanban-card"
          onClick={handleCardClick}
          style={{ cursor: 'pointer' }}
        >
          <Card.Body>
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div>
                <h6 className="mb-1">
                  #
                  {currentArea === 'purchases'
                    ? getPurchaseDisplayId(item as PurchaseRequest)
                    : item.id}
                </h6>
                <p className="mb-0 ticket-title">{item.title}</p>
              </div>
              <Badge bg={getPriorityColor(item.priority)} className="ms-2">
                {item.priority}
              </Badge>
            </div>
            
            {item.description && (
              <p className="ticket-description text-muted mb-2">
                {item.description.length > 100
                  ? `${item.description.substring(0, 100)}...`
                  : item.description}
              </p>
            )}
            
            <div className="d-flex flex-wrap gap-1 mb-2">
              <Badge bg={getStatusColor(item.status)}>
                {getStatusLabel(item.status)}
              </Badge>
              {item.current_step && (
                <Badge bg="info" className="border border-info">
                  {item.current_step}
                </Badge>
              )}
            </div>
            
            <div className="ticket-meta">
              <small className="text-muted d-block">
                <strong>Criado por:</strong> {item.creator?.name || 'N/A'}
              </small>
              {item.assignee && (
                <small className="text-muted d-block">
                  <strong>Atribuído a:</strong> {item.assignee.name}
                </small>
              )}
              {item.assigned_group_id && (
                <small className="text-muted d-block">
                  <strong>Grupo:</strong> {
                    groups.find(g => g.id === item.assigned_group_id)?.name || 'N/A'
                  }
                </small>
              )}
              <small className="text-muted d-block">
                <strong>Data:</strong> {formatDate(item.created_at)}
              </small>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  };

  // Componente para coluna droppable
  const DroppableColumn: React.FC<{ column: KanbanColumn; items: (Ticket | PurchaseRequest)[] }> = ({ column, items }) => {
    const {
      setNodeRef,
      isOver,
    } = useDroppable({ id: column.id });

    const isHighlighted = overColumnId === column.id;

    return (
      <div 
        ref={setNodeRef}
        className={`kanban-column ${isOver || isHighlighted ? 'drag-over' : ''}`}
      >
        <div className="kanban-column-header">
          <div className="d-flex align-items-center">
            <h5 className="mb-0">{column.name}</h5>
            <Badge bg="secondary" className="ms-2">{items.length}</Badge>
          </div>
        </div>
        <div className="kanban-column-content">
          <SortableContext items={items.map(i => i.id.toString())} strategy={verticalListSortingStrategy}>
            {items.length === 0 ? (
              <div className="kanban-empty-state">
                <p className="text-muted mb-0">
                  {currentArea === 'purchases' ? 'Nenhuma requisição' : 'Nenhum ticket'}
                </p>
              </div>
            ) : (
              items.map((item) => (
                <SortableItemCard key={item.id} item={item} />
              ))
            )}
          </SortableContext>
        </div>
      </div>
    );
  };

  return (
    <div className="reports-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-0">Relatórios - Kanban</h2>
          <p className="text-muted mb-0">
            {currentArea === 'purchases' 
              ? 'Visualize suas requisições de compras em formato Kanban, organizadas por fluxo de trabalho'
              : 'Visualize seus tickets em formato Kanban, organizados por fluxo de trabalho'}
          </p>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'Ocultar' : 'Mostrar'} Filtros
            {activeFiltersCount > 0 && (
              <Badge bg="primary" className="ms-2">{activeFiltersCount}</Badge>
            )}
          </Button>
          {activeFiltersCount > 0 && (
            <Button variant="outline-danger" onClick={clearFilters}>
              Limpar Filtros
            </Button>
          )}
        </div>
      </div>

      {/* Modal de seleção de fluxo */}
      <Modal show={showWorkflowModal} onHide={() => {}} backdrop="static" keyboard={false}>
        <Modal.Header>
          <Modal.Title>Selecione um Fluxo de Trabalho</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            Para visualizar o Kanban, é necessário selecionar um fluxo de trabalho.
          </p>
          <Form.Group>
            <Form.Label>
              <strong>Fluxo de Trabalho</strong> <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={selectedWorkflow}
              onChange={(e) => {
                const value = e.target.value ? parseInt(e.target.value) : '';
                setSelectedWorkflow(value);
                if (value) {
                  setShowWorkflowModal(false);
                }
              }}
            >
              <option value="">Selecione um fluxo...</option>
              {workflows.map((workflow) => (
                <option key={workflow.id} value={workflow.id}>
                  {workflow.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          {workflows.length === 0 && (
            <div className="alert alert-info mt-3">
              <strong>Nenhum fluxo ativo encontrado.</strong> Crie um fluxo de trabalho na seção "Fluxos" para começar.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowWorkflowModal(false);
            }}
          >
            Visualizar Todos
          </Button>
          <Button 
            variant="primary" 
            onClick={() => {
              if (selectedWorkflow) {
                setShowWorkflowModal(false);
              }
            }}
            disabled={!selectedWorkflow}
          >
            Confirmar
          </Button>
        </Modal.Footer>
      </Modal>

      {showFilters && (
        <Card className="mb-4 filter-card">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <strong>Filtros Avançados</strong>
            <small className="text-muted">
              {items.length} {currentArea === 'purchases' ? 'requisição(ões)' : 'ticket(s)'} encontrado(s)
            </small>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label>
                    <strong>Fluxo de Trabalho</strong> <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={selectedWorkflow}
                    onChange={(e) => setSelectedWorkflow(e.target.value ? parseInt(e.target.value) : '')}
                  >
                    <option value="">Selecione um fluxo para visualizar o Kanban</option>
                    {workflows.map((workflow) => (
                      <option key={workflow.id} value={workflow.id}>
                        {workflow.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    O Kanban será organizado baseado nos steps do fluxo selecionado
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label>Buscar</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Título ou descrição; para ID use #número"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="aberto">Aberto</option>
                    <option value="em_andamento">Em Andamento</option>
                    <option value="aguardando_informacao_usuario">Aguardando Informação do Usuário</option>
                    <option value="aguardando_terceiros">Aguardando Terceiros</option>
                    <option value="aguardando_evidencia">Aguardando Evidência</option>
                    <option value="aguardando_problema">Aguardando Problema</option>
                    <option value="aguardando_implantacao">Aguardando Implantação</option>
                    <option value="resolvido">Resolvido</option>
                    <option value="fechado">Fechado</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Prioridade</Form.Label>
                  <Form.Select
                    value={filterPriority}
                    onChange={(e) => setFilterPriority(e.target.value)}
                  >
                    <option value="">Todas</option>
                    <option value="p1">P1</option>
                    <option value="p2">P2</option>
                    <option value="p3">P3</option>
                    <option value="p4">P4</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Grupo</Form.Label>
                  <Form.Select
                    value={filterGroup}
                    onChange={(e) => setFilterGroup(e.target.value ? parseInt(e.target.value) : '')}
                  >
                    <option value="">Todos</option>
                    {groups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Atribuído a</Form.Label>
                  <Form.Select
                    value={filterAssignedTo}
                    onChange={(e) => setFilterAssignedTo(e.target.value ? parseInt(e.target.value) : '')}
                  >
                    <option value="">Todos</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Criado por</Form.Label>
                  <Form.Select
                    value={filterUser}
                    onChange={(e) => setFilterUser(e.target.value ? parseInt(e.target.value) : '')}
                  >
                    <option value="">Todos</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Data Inicial</Form.Label>
                  <Form.Control
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Data Final</Form.Label>
                  <Form.Control
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {!selectedWorkflow ? (
        <Card className="empty-state-card">
          <Card.Body className="text-center py-5">
            <div className="mb-4">
              <div className="empty-state-icon">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <line x1="9" y1="3" x2="9" y2="21"/>
                  <line x1="15" y1="3" x2="15" y2="21"/>
                </svg>
              </div>
            </div>
            <h4 className="mb-3">Selecione um Fluxo de Trabalho</h4>
            <p className="text-muted mb-4">
              Para visualizar o Kanban, selecione um fluxo de trabalho nos filtros acima.
              <br />
              O Kanban será organizado automaticamente baseado nos steps do fluxo escolhido.
            </p>
            {workflows.length === 0 && (
              <div className="alert alert-info">
                <strong>Nenhum fluxo ativo encontrado.</strong> Crie um fluxo de trabalho na seção "Fluxos" para começar.
              </div>
            )}
          </Card.Body>
        </Card>
      ) : (
        <div className="kanban-container">
          {selectedWorkflowData && (
            <div className="workflow-info mb-3">
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-2">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <strong>Fluxo:</strong> {selectedWorkflowData.name}
                      {selectedWorkflowData.description && (
                        <span className="text-muted ms-2">- {selectedWorkflowData.description}</span>
                      )}
                    </div>
                    <Badge bg="primary">
                      {kanbanColumns.length} {kanbanColumns.length === 1 ? 'coluna' : 'colunas'}
                    </Badge>
                  </div>
                </Card.Body>
              </Card>
            </div>
          )}
          {items.length === 0 ? (
            <Card className="empty-state-card">
              <Card.Body className="text-center py-5">
                <div className="mb-3">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-muted">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                </div>
                <h5>{currentArea === 'purchases' ? 'Nenhuma requisição encontrada' : 'Nenhum ticket encontrado'}</h5>
                <p className="text-muted">
                  {currentArea === 'purchases' 
                    ? 'Ajuste os filtros para encontrar requisições ou crie novas requisições na seção "Requisições de Compras".'
                    : 'Ajuste os filtros para encontrar tickets ou crie novos tickets na seção "Tickets".'}
                </p>
              </Card.Body>
            </Card>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCorners}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
            >
              <div className="kanban-board">
                {kanbanColumns.map((column) => {
                  const columnItems = ticketsByColumn[column.id] || [];
                  return (
                    <DroppableColumn
                      key={column.id}
                      column={column}
                      items={columnItems}
                    />
                  );
                })}
              </div>
              <DragOverlay>
                {(draggingTicket || draggingPurchaseRequest) ? (
                  <Card className="kanban-card" style={{ opacity: 0.8, transform: 'rotate(5deg)' }}>
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <h6 className="mb-1">#{(draggingTicket || draggingPurchaseRequest)?.id}</h6>
                          <p className="mb-0 ticket-title">{(draggingTicket || draggingPurchaseRequest)?.title}</p>
                        </div>
                        <Badge bg={getPriorityColor((draggingTicket || draggingPurchaseRequest)?.priority)} className="ms-2">
                          {(draggingTicket || draggingPurchaseRequest)?.priority}
                        </Badge>
                      </div>
                    </Card.Body>
                  </Card>
                ) : null}
              </DragOverlay>
            </DndContext>
          )}
        </div>
      )}
    </div>
  );
};

export default Reports;

